import { FC, useEffect, useState } from 'react';

import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { groupByKey } from '../utils';

import {
  BaseplanOverview,
  getScenarioBaseplanOverview
} from 'RestAPIs/capacity-plan/analyzer';

import { scenarioAtom } from '../atoms';

import { Box, Button } from '@mui/material';

import {
  LoadingOverlay,
  showSnackbar,
  SkeletonWithIcon
} from 'components/shared';
import NoDataPlaceholder from 'components/shared/NoDataPlaceholder';

import BaseplanChart from './components/BaseplanChart';
import BaseplanTable from './components/BaseplanTable';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';

interface BaseplanTotalProps {
  refreshTotalsGraph: boolean;
  setRefreshTotalsGraph: (refresh: boolean) => void;
}

const BaseplanTotals: FC<BaseplanTotalProps> = ({
  refreshTotalsGraph,
  setRefreshTotalsGraph
}) => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom);

  const [baseplans, setBaseplans] = useState<BaseplanOverview[] | []>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewGraph, setIsViewGraph] = useState(true);
  const { year, aggregate } = useYearQuarterNavigation();

  const loadBaseplans = async (scenarioId: number, year: number) => {
    if (baseplans.length === 0) {
      setIsLoading(true);
    }

    try {
      const { data } = await getScenarioBaseplanOverview({
        scenarioId,
        year,
        aggregateOn: aggregate
      });

      const baseplansByType = groupByKey(data, 'isBasePlan', t).sort(
        (a, b) => a.isBasePlan - b.isBasePlan
      );
      setBaseplans(baseplansByType);
    } catch {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (scenario) {
      loadBaseplans(scenario.scenarioId, year);
    }
  }, [scenario, year, aggregate]);

  useEffect(() => {
    if (scenario && refreshTotalsGraph) {
      loadBaseplans(scenario.scenarioId, year);
      setRefreshTotalsGraph(false);
    }
  }, [refreshTotalsGraph]);

  return (
    <Box>
      <LoadingOverlay loading={isLoading} />
      <Box
        mb={4}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="end"
      >
        <Button variant="outlined" onClick={() => setIsViewGraph(!isViewGraph)}>
          {isViewGraph ? t('showTable') : t('showGraph')}
        </Button>
      </Box>
      {isLoading && isViewGraph ? (
        <SkeletonWithIcon height={400} />
      ) : (
        <>
          {baseplans.length > 0 ? (
            <>
              {isViewGraph ? (
                <BaseplanChart baseplans={baseplans} />
              ) : (
                <BaseplanTable baseplans={baseplans} />
              )}
            </>
          ) : (
            <StyledPaper>
              <NoDataPlaceholder title={t('baseplan.baseplanChart.noData')} />
            </StyledPaper>
          )}
        </>
      )}
    </Box>
  );
};

export default BaseplanTotals;
